/**
 * Titre affiché d'un widget d'onglet personnalisé.
 *
 * Une instance de widget persiste un champ `name`, initialisé par le panneau de configuration
 * avec le libellé PAR DÉFAUT du catalogue. Un widget jamais renommé se retrouve donc en base
 * avec un `name` non vide qu'il ne faut PAS traiter comme un renommage : sinon il masque la
 * traduction et le titre reste figé dans la langue de sa création.
 *
 * Comparer `name` au seul libellé COURANT du catalogue ne suffit pas : renommer une entrée du
 * catalogue transforme rétroactivement toutes les instances déjà enregistrées en « widget
 * renommé par l'utilisateur ». C'est ce qui est arrivé à `widget-agent-status-breakdown`
 * (« Répartition des statuts par agent » → « Statuts par agent », commit 3c19e01) : les
 * instances existantes affichaient l'ancien libellé français jusque dans l'interface anglaise.
 *
 * On considère donc comme libellé par défaut TOUT libellé connu du widget : celui du catalogue
 * ET ses traductions dans toutes les langues chargées (l'ancien nom du catalogue est en pratique
 * la traduction française, ce qui couvre aussi les renommages passés).
 */

const defaultNamesFor = (i18n, widgetId, catalogName) => {
    const names = new Set();
    if (catalogName) names.add(catalogName);

    const languages = Object.keys(i18n?.store?.data || i18n?.options?.resources || {});
    languages.forEach((lng) => {
        const translated = i18n?.getResource?.(lng, 'translation', `WIDGET_NAMES.${widgetId}`);
        if (typeof translated === 'string' && translated) names.add(translated);
    });

    return names;
};

/** `true` uniquement si l'utilisateur a VRAIMENT renommé le widget. */
export const isCustomWidgetName = (i18n, { widgetId, name, catalogName }) =>
    !!name && !defaultNamesFor(i18n, widgetId, catalogName).has(name);

/** Renommage utilisateur s'il existe, sinon le libellé traduit du widget. */
export const resolveWidgetTitle = (t, i18n, { widgetId, name, catalogName, fallback }) =>
    isCustomWidgetName(i18n, { widgetId, name, catalogName })
        ? name
        : t(`WIDGET_NAMES.${widgetId}`, { defaultValue: catalogName || fallback || widgetId });
